import { __ } from '@wordpress/i18n';
import { registerPaymentMethod } from '@woocommerce/blocks-registry';
import { decodeEntities } from '@wordpress/html-entities';
import { getSetting } from '@woocommerce/settings';
import { useEffect } from '@wordpress/element'
import {
    get_paysafe_label,
    init_paysafe_fields,
    log_paysafe_error,
    main_paysafe_checkout_success_handler,
    set_log_nonce,
} from "./paysafe-common";

const paymentMethodId = "paysafe";
const settings = getSetting( 'paysafe_data', {} );
set_log_nonce(settings.nonce);
const log_error_endpoint = settings.log_errors === true ? (settings.log_error_endpoint || null) : null;
const is_hosted_integration = settings.integration_type === 'paysafe_js';

let is_cvv_verification = !!settings.cvv_verification;
let current_cart_data = {};
let hosted_payment_instance;

const init_paysafe_hosted_integration = function() {
    // if the page doesn't have the form, don't load it
    if (!jQuery('#paysafe-hosted-payment-form').length) {
        return;
    }

    if (typeof(paysafe) === "undefined" || typeof(paysafe.fields) === "undefined") {
        setTimeout(function() {
            init_paysafe_hosted_integration();
        }, 1000);
        return;
    }

    // reset the CVV field initialization for saved cards
    cvv_form_initialized = false;

    const paysafeOptions = {
        // You must provide currencyCode to the Paysafe JS SDK to enable the Payment API integration
        currencyCode: settings.currency_code,

        // select the Paysafe test / sandbox environment
        environment: settings.test_mode ? 'TEST' : 'LIVE',

        transactionSource: 'WooCommerceJs',

        // set the CSS selectors to identify the payment field divs above
        // set the placeholder text to display in these fields
        fields: {
            cardNumber: {
                selector: '#cardNumber',
                placeholder: 'Card number',
                separator: ' ',
            },
            expiryDate: {
                selector: '#expiryDate',
                placeholder: 'MM/YY',
            },
            cvv: {
                selector: '#cvv',
                placeholder: 'CVV',
                optional: false,
            },
        },
        style: {
            input: {
                "font-family": "sans-serif",
                "font-weight": "normal",
                "font-size": "16px"
            }
        },
        accounts: {
            default: settings.account_id,
        },
    };
    if (settings.initialization_timeout != null && Number(settings.initialization_timeout) >= 1000) {
        paysafeOptions.initializationTimeout = Number(settings.initialization_timeout);
    }
    // initialize the hosted iframes using the SDK setup function
    init_paysafe_fields(settings, paysafeOptions, current_cart_data).then( instance => {
        hosted_payment_instance = instance;
    });
}

let cvv_verification_instance = null;
let cvv_form_initialized = false;
const init_paysafe_cvv_verification = function() {
    // if the page doesn't have the form, don't load it
    if (!jQuery('#cvv-verification-container').length) {
        return;
    }

    if (typeof(paysafe) === "undefined" || typeof(paysafe.fields) === "undefined") {
        setTimeout(function() {
            init_paysafe_cvv_verification();
        }, 1000);
        return;
    }

    // mark that the CVV initialization has been done
    cvv_form_initialized = true;
    cvv_verification_instance = null;

    const paysafe_js_options = {
        // You must provide currencyCode to the Paysafe JS SDK to enable the Payment API integration
        currencyCode: settings.currency_code,

        // select the Paysafe test / sandbox environment
        environment: settings.test_mode ? 'TEST' : 'LIVE',

        transactionSource: 'WooCommerceJs',

        fields: {
            cardNumber: {
                selector: '#cardNumber',
                placeholder: 'Card number',
                separator: ' ',
                optional: true,
            },
            expiryDate: {
                selector: '#expiryDate',
                placeholder: 'MM/YY',
                optional: true,
            },
            cvv: {
                selector: '#cvv',
                placeholder: 'CVV',
                optional: !is_cvv_verification,
            },
        },
        accounts: {
            default: settings.account_id,
        },
    };
    if (settings.initialization_timeout != null && Number(settings.initialization_timeout) >= 1000) {
        paysafe_js_options.initializationTimeout = Number(settings.initialization_timeout);
    }

    // if the CVV verification is not enabled, don't load it
    if (typeof paysafe === 'undefined' || typeof paysafe.fields === 'undefined') {
        return;
    }

    // initialize the hosted iframes using the SDK setup function
    init_paysafe_fields(settings, paysafe_js_options, current_cart_data)
        .then( instance => {
            cvv_verification_instance = instance;
        })
        .catch(error => {
            if (parseInt(error.code) === 9073 && is_cvv_verification) {
                // account not configured properly
                is_cvv_verification = false;
                init_paysafe_cvv_verification();
            }
        });
}

const Content = ( props ) => {
    const { eventRegistration, emitResponse } = props;
    const { onCheckoutSuccess, onPaymentSetup } = eventRegistration;

    useEffect(() => {
        if (is_cvv_verification) {
            // reset the CVV field initialization for saved cards
            cvv_form_initialized = false;
        }
        if (is_hosted_integration) {
            init_paysafe_hosted_integration();
        }

        const unsubscribe = onPaymentSetup( async (response) => {
            if (is_hosted_integration) {
                if (hosted_payment_instance) {
                    // check if the hosted integration is filled in and the data is valid
                    let isTokenizationAvailable = hosted_payment_instance.areAllFieldsValid();
                    if (isTokenizationAvailable) {
                        let holderNameInput = document.getElementById('holderNameInput');
                        let isHolderNameValid = holderNameInput && holderNameInput.value && holderNameInput.value.length && holderNameInput.value.length >= 2 && holderNameInput.value.length <= 160;

                        if (isHolderNameValid) {
                            return {
                                type: emitResponse.responseTypes.SUCCESS,
                            };
                        } else {
                            log_paysafe_error(log_error_endpoint, __("The payment process failed. Card holder name must have between 2 and 160 characters!", "paysafe-checkout"), {'tokenization_stage': 2});
                            return {
                                type: emitResponse.responseTypes.ERROR,
                                message: __("The payment process failed. Card holder name must have between 2 and 160 characters!", "paysafe-checkout"),
                            };
                        }
                    } else {
                        log_paysafe_error(log_error_endpoint, __("The payment process failed. Card holder name or card details failed verification!", "paysafe-checkout"), {'tokenization_stage': 2});
                        return {
                            type: emitResponse.responseTypes.ERROR,
                            message: __("The payment process failed. Please enter your card details and try again", "paysafe-checkout"),
                        };
                    }
                } else {
                    log_paysafe_error(log_error_endpoint, __("The payment process failed.", "paysafe-checkout"), {'tokenization_stage': 3});
                    return {
                        type: emitResponse.responseTypes.ERROR,
                        message: __("The payment process failed. Please reload the page and try again.", "paysafe-checkout"),
                    };
                }
            }
        });

        return () => {
            unsubscribe();
        };
    }, [
        emitResponse.responseTypes.ERROR,
        emitResponse.responseTypes.SUCCESS,
        onPaymentSetup
    ]);

    useEffect(() => {
        const unsubscribe = onCheckoutSuccess( (response) => {
            const paysafe_checkout_options = {
                displayPaymentMethods: [
                    'card'
                ],
                paymentMethodDetails: {
                    card: {
                        accountId: settings.account_id,
                    }
                }
            };

            if (is_hosted_integration) {
                let save_card_checkbox = document.getElementById('paysafe_hosted_save_card');
                if (save_card_checkbox && save_card_checkbox.checked) {
                    paysafe_checkout_options.save_card = true;
                }

                paysafe_checkout_options.hosted_payment_instance = hosted_payment_instance;
            }

            return main_paysafe_checkout_success_handler(
                response,
                current_cart_data,
                settings,
                paysafe_checkout_options,
                is_hosted_integration,
                emitResponse
            );
        });

        return () => {
            unsubscribe();
        };
    }, [
        emitResponse.responseTypes.ERROR,
        emitResponse.responseTypes.SUCCESS,
        '',
        [],
        onCheckoutSuccess,
    ]);

    let descriptionForDisplay = [];

    if (settings.description) {
        descriptionForDisplay.push(
            <div>{ decodeEntities( settings.description || '' ) }</div>
        );
    }

    if (settings.description && settings.test_mode) {
        descriptionForDisplay.push(<hr></hr>);
    }

    if( settings.test_mode ) {
        descriptionForDisplay.push(
            <div style={{ textAlign: "justify", marginTop: "5px" }} >
                <span>{
                    __(
                        'Paysafe Checkout is in TEST MODE. Use the test Visa card 4000000000001091 with any expiry date, CVC, email, or OTP token. Important notice: Please use only TEST CARDS for testing. You can find other test cards ',
                        'paysafe-checkout'
                    )
                }</span>
                <a href="https://developer.paysafe.com/en/api-docs/cards/test-and-go-live/test-cards/" target="_blank">{
                    __( 'here. ', 'paysafe-checkout')
                } </a>
            </div>
        );
    }

    if( settings.test_mode && is_hosted_integration ) {
        descriptionForDisplay.push(<p></p>);
    }

    if (is_hosted_integration) {
        descriptionForDisplay.push(wp.element.RawHTML({ children: settings.integration_hosted_form }));
    } else {
        if (settings.is_subscription_payment) {
            descriptionForDisplay.push(
                <p style={{ fontSize: "0.8em", marginBottom: "0" }}>
                    <b>{__('Note:', 'paysafe-checkout')}</b>
                    {__('This order includes a subscription.', 'paysafe-checkout')}
                    {__('By proceeding with payment, your card details will be securely saved for future recurring payments.', 'paysafe-checkout')}
                    <br />
                    {__('Card data is stored and processed securely by Paysafe in compliance with PCI DSS standards and according to the merchant’s', 'paysafe-checkout')}
                    &nbsp;
                    {__('Terms and Conditions', 'paysafe-checkout')}
                    &nbsp;
                    {__('and', 'paysafe-checkout')}
                    &nbsp;
                    {__('Privacy Policy.', 'paysafe-checkout')}
                </p>
            );
        }
    }

    return descriptionForDisplay;
};

const SavedTokenComponent = ( props ) => {
    const { eventRegistration, emitResponse } = props;
    const { onCheckoutSuccess, onPaymentSetup } = eventRegistration;

    useEffect(() => {
        if (!cvv_form_initialized) {
            cvv_form_initialized = true;
            init_paysafe_cvv_verification();
        }

        const unsubscribe = onPaymentSetup( () => {
            if (is_cvv_verification) {
                if (cvv_verification_instance) {
                    // check if the CVV verification is filled in and the data is valid
                    let is_tokenization_available = cvv_verification_instance.areAllFieldsValid();
                    if (!is_tokenization_available) {
                        log_paysafe_error(log_error_endpoint, __("The payment process failed. No CVV entered", "paysafe-checkout"), {'tokenization_stage': 2});
                        return {
                            type: emitResponse.responseTypes.ERROR,
                            message: __("The payment process failed. Please enter your saved card CVV and try again", "paysafe-checkout"),
                        };
                    }
                } else {
                    log_paysafe_error(log_error_endpoint, __("The payment process failed.", "paysafe-checkout"), {'tokenization_stage': 3});
                    return {
                        type: emitResponse.responseTypes.ERROR,
                        message: __("The payment process failed. Please reload the page and try again.", "paysafe-checkout"),
                    };
                }
            }
        });


        return () => {
            unsubscribe();
        };
    }, [
        emitResponse.responseTypes.ERROR,
        emitResponse.responseTypes.SUCCESS,
        onPaymentSetup,
    ]);

    useEffect(() => {
        const unsubscribe = onCheckoutSuccess( (response) => {
            const redirect_url = response && response.processingResponse && response.processingResponse.paymentDetails && response.processingResponse.paymentDetails.redirect ? response.processingResponse.paymentDetails.redirect : null;

            // in case of BE token payments, we are done at this point
            // redirect to success page
            if (redirect_url) {
                window.location = redirect_url;
                return {
                    type: emitResponse.responseTypes.SUCCESS,
                };
            }

            const cvv_payment_options = {
                hosted_payment_instance: cvv_verification_instance,
                use_single_use_customer_token: true,
                payment_toke_from: response
                    && response.processingResponse
                    && response.processingResponse.paymentDetails
                    && response.processingResponse.paymentDetails.paysafe_token
                    ? response.processingResponse.paymentDetails.paysafe_token : '',
            };

            return main_paysafe_checkout_success_handler(
                response,
                current_cart_data,
                settings,
                cvv_payment_options,
                true,
                emitResponse
            );
        });

        return () => {
            unsubscribe();
        };
    }, [
        emitResponse.responseTypes.ERROR,
        emitResponse.responseTypes.SUCCESS,
        '',
        [],
        onCheckoutSuccess,
    ]);

    if (is_cvv_verification && typeof paysafe !== 'undefined' && typeof paysafe.fields !== 'undefined') {
        return [
            <div id="cvv-verification-container">
                <b>{__('CVV', 'paysafe-checkout')}</b>
                <div id="holderName" className="paysafe-input-field optional-field" style={{display: "none"}}></div>
                <div id="cardNumber" className="paysafe-input-field optional-field" style={{display: "none"}}></div>
                <div id="expiryDate" className="paysafe-input-field optional-field" style={{display: "none"}}></div>
                <div className="paysafe-cc-form-exp-cvv-row">
                    <div className="paysafe-cc-form-exp-cvv-box1"></div>
                    <div className="paysafe-cc-form-exp-cvv-box2">
                        <div id="cvv" className="paysafe-input-field"></div>
                    </div>
                    <div className="paysafe-cc-form-exp-cvv-box3">
                        <img className="paysafe-cc-form-cvv-image"
                             src={settings.paysafe_base_url + "assets/img/cvv.svg"}
                             alt="CVV"/>
                    </div>
                </div>
            </div>
        ];
    }

    return [
        <div id="cvv-verification-container">
            <div id="holderName" className="paysafe-input-field optional-field"></div>
            <div id="cardNumber" className="paysafe-input-field optional-field"></div>
            <div id="expiryDate" className="paysafe-input-field optional-field"></div>
            <div id="cvv" className="paysafe-input-field optional-field"></div>
        </div>
    ];
};

const PaysafePayments = {
    name: paymentMethodId,
    paymentMethodId: paymentMethodId,
    label: get_paysafe_label(settings.title, settings.icon),
    content: <Content />,
    edit: <Content />,
    savedTokenComponent: <SavedTokenComponent />,
    canMakePayment: ( cartData => {
        current_cart_data = cartData;

        return true;
    }),
    ariaLabel: __('Paysafe Checkout', 'paysafe-checkout' ),
    supports: {
        features: settings.supports,
    },
};

registerPaymentMethod( PaysafePayments );
